import BannerSection from '@/pages/dashboard/widgets/BannerSection'
import { Skeleton } from '@/components/ui/skeleton'
import useAppStore from '@/store/useAppStore'
import useGetStatus from '@/services/api/cash-flow/useGetStatus'
import useVaultSelection from '@/pages/dashboard/useVaultSelection'
import CardErrorState from '@/pages/dashboard/widgets/CardErrorState'

interface ToneStyle {
  chip: string
  leftBorder: string
}

// bg-* tone tokens (e.g. bg-red-lowest) are wired into Tailwind's config and
// work as plain utility classes; text-*/border-* are not (only backgroundColor
// was extended with this palette, see tailwind.config.cjs), so those use
// arbitrary-value hsl literals taken straight from the design source instead —
// the same pattern already used for the pre-existing caution text color below.
const TONE_STYLE: Record<string, ToneStyle> = {
  positive: {
    chip: 'border border-[hsla(137,57%,41%,.24)] bg-green-tr-12 text-[hsl(138,71%,23%)]',
    leftBorder: 'border-l-[hsl(137,57%,41%)]',
  },
  neutral: {
    chip: 'border border-[hsla(215,19%,35%,.24)] bg-gray-tr-12 text-txt-secondary',
    leftBorder: 'border-l-[hsl(213,27%,84%)]',
  },
  caution: {
    chip: 'border border-[hsla(44,66%,50%,.24)] bg-yellow-tr-12 text-[hsl(40,100%,30%)]',
    leftBorder: 'border-l-[hsl(44,66%,50%)]',
  },
  negative: {
    chip: 'border border-[hsla(359,95%,63%,.32)] bg-red-lowest text-[hsl(356,72%,47%)]',
    leftBorder: 'border-l-[hsl(359,95%,63%)]',
  },
}

interface FirstBreach {
  period_offset: number
  vault_id: number | null
  closing_balance: number
}

interface StatusData {
  state: 'critical' | 'alert' | 'watch' | 'healthy' | 'building'
  label: string
  tone: string
  trend_pct_per_month: number | null
  first_breach: FirstBreach | null
  article_url: string | null
}

/**
 * Splits the headline into { prefix, linkLabel, suffix } — only the breach
 * reference itself (e.g. "week 12") is a link, matching the design; the rest
 * of the sentence is plain text. linkLabel is null when there's nothing to
 * jump to (watch/healthy/building never carry a first_breach).
 */
function sentence(
  data: StatusData,
  periodType: string,
  vaultName?: string,
): { prefix: string; linkLabel: string | null; suffix: string } {
  const unit = periodType === 'month' ? 'month' : 'week'
  const pct = data.trend_pct_per_month !== null ? Math.abs(data.trend_pct_per_month).toFixed(1) : '0.0'

  switch (data.state) {
    case 'building':
      return { prefix: `Growing ${pct}% a month across the horizon`, linkLabel: null, suffix: '' }
    case 'healthy':
      return { prefix: 'Steady across the horizon', linkLabel: null, suffix: '' }
    case 'watch':
      return { prefix: `Declining ${pct}% a month`, linkLabel: null, suffix: '' }
    case 'alert':
      return {
        prefix: `${vaultName ?? 'A vault'} goes negative in `,
        linkLabel: `${unit} ${data.first_breach?.period_offset}`,
        suffix: '',
      }
    case 'critical':
      return {
        prefix: 'Total balance goes negative in ',
        linkLabel: `${unit} ${data.first_breach?.period_offset}`,
        suffix: '',
      }
    default:
      return { prefix: '', linkLabel: null, suffix: '' }
  }
}

export default function StatusCard() {
  const offset = useAppStore((state) => state.offset)
  const numberOfPeriods = useAppStore((state) => state.numberOfPeriods)
  const periodType = useAppStore((state) => state.periodType)
  const setHighlightedPeriodOffset = useAppStore((state) => state.setHighlightedPeriodOffset)

  const { data, status, refetch } = useGetStatus({ offset, numberOfPeriods, periodType })
  // Status only ever names a vault by id — resolve the name from the same
  // vault list the popover/legend already use, rather than round-tripping
  // it through the API.
  const { vaultsFlat } = useVaultSelection()

  function jumpToBreach() {
    if (!data?.first_breach) return
    setHighlightedPeriodOffset(data.first_breach.period_offset)
    document
      .getElementById('projected-balance-card')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const isCritical = data?.state === 'critical'
  const tone = TONE_STYLE[data?.tone ?? 'neutral'] ?? TONE_STYLE.neutral

  return (
    <BannerSection
      label="Status"
      className={`border-l-4 ${tone.leftBorder} ${isCritical ? 'bg-red-lowest' : ''}`}
    >
      {status === 'pending' && (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-3">
            <Skeleton className="h-6 w-20 rounded-md" />
            <Skeleton className="h-4 w-40" />
          </div>
          <div className="text-[11px] text-txt-secondary">
            Loading — each card resolves on its own
          </div>
        </div>
      )}

      {status === 'error' && <CardErrorState onRetry={() => refetch()} />}

      {status === 'success' && data && (
        <div className="flex flex-col gap-2">
          {/* Chip and sentence share a row — the state and what it means read
              as one statement rather than a label stacked over a caption. */}
          <div className="flex flex-wrap items-center gap-2.5">
            {isCritical ? (
              <span className="inline-flex w-fit shrink-0 items-center rounded-md bg-red-default px-2.5 py-0.5 text-xs font-bold text-white">
                {data.label}
              </span>
            ) : (
              <span
                className={`inline-flex w-fit shrink-0 items-center rounded-md px-2.5 py-0.5 text-xs font-bold ${tone.chip}`}
              >
                {data.label}
              </span>
            )}

            {(() => {
              const parts = sentence(
                data,
                periodType,
                vaultsFlat.find((v) => v.id === data.first_breach?.vault_id)?.name,
              )
              return (
                <span className="text-[17px] font-semibold leading-snug tracking-[-0.01em] text-txt-primary">
                  {parts.prefix}
                  {parts.linkLabel && (
                    <button
                      type="button"
                      onClick={jumpToBreach}
                      className="text-txt-accent underline decoration-dotted underline-offset-2"
                    >
                      {parts.linkLabel}
                    </button>
                  )}
                  {parts.suffix}
                </span>
              )
            })()}
          </div>

          {data.article_url && (
            <a
              href={data.article_url}
              target="_blank"
              rel="noreferrer"
              className="text-[12.5px] font-semibold text-txt-accent"
            >
              How to fix this →
            </a>
          )}
        </div>
      )}
    </BannerSection>
  )
}
